import React from 'react';
import PropTypes from 'prop-types';
import MakeAsyncFunction from 'react-redux-promise-listener';
import { withRouter } from 'next/router';
import { connect } from 'react-redux';
import { Beforeunload } from 'react-beforeunload';
import FormWizard from '../../01_atoms/FormWizard';
import { filterActiveMethods, methods, PAYMENT_MODE } from '../../../utils/payments';
import { DEFAULT_COUNTRY_CODE, SD, RG, DONATION_FORM_STEPS } from '../../../utils/constants';
import AmountPersonalInfoStep from './steps/01_AmountPersonalInfoStep';
import AddressStep from './steps/02_AddressStep';
import ContactPreferencesStep from './steps/03_ContactPreferencesStep';
import GiftAidStep from '../../02_molecules/GiftAidQuestion';
import PaymentsStep from './steps/06_PaymentsStep';
import { getDefaultCountry } from '../../../utils/address-schema';
import {
  pushPurchase as gtmPushPurchase,
  pushReturnToDonationPrompt as gtmPushReturnToDonationPrompt,
} from '../../../utils/gtm';
import fetch from '../../../utils/fetch';
import unexpectedErrorMessage from '../../../utils/unexpectedErrorMessage';
import { hasShortformValuesInQuery } from '../../../utils/shortform';
import {
  buildStepPath,
  getFundraisingCostsExplanation,
  getGiftAidAmount,
  getPreferencesForView,
  isFundraisingCostsVisible,
  normalisePreferencesValues,
} from '../../../utils/donationForm';
import { getAttribution, isGeneralDonation, isValidGUID } from '../../../utils/attribution';
import LoadingText from '../../01_atoms/LoadingText';
import Popup from '../../02_molecules/Popup';
import PaymentModeWarning from '../../01_atoms/PaymentModeWarning';
import {
  donationFormReset,
  donationFormStateUpdate,
  donationFormStepValidationErrors,
  donationFormSubmitError,
} from '../../../store/Slices/cnet/donationFormSlice';
import {
  cancelPaymentOnDrupal,
  completeOrder,
  hasReturnUrlValuesInQuery,
} from '../../02_molecules/RevolutStripeForm';
import rollbar from '../../../utils/rollbar-agent';
import { promiseListener } from '../../../store/store';
import { sessionStorageSetItem } from '../../../utils/web-storage';
import { registerSourceCodeField } from '../../../utils/donationFormSourceCodeExperiment';

class DonationForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading:
        hasShortformValuesInQuery(props.router.query) ||
        hasReturnUrlValuesInQuery(props.router.query),
      email: '',
      name: '',
    };

    this.handleSuccessReturn = this.handleSuccessReturn.bind(this);
  }

  async componentDidMount() {
    const { router, dispatch = () => {} } = this.props;
    const { isLoading } = this.state;

    if (hasShortformValuesInQuery(router.query)) {
      try {
        // Resolving name and email from CRM.
        const response = await fetch(`/falcon_nfp365_crm/contact/${router.query.dsn}`);
        if (response.body && response.body.email && response.body.name) {
          this.setState({
            email: response.body.email,
            name: response.body.name,
            isLoading: false,
          });
        } else {
          if (isLoading) {
            this.setState({
              isLoading: false,
            });
          }
        }
      } catch {
        // Error is sent to Rollbar in 'fetch'.
        this.setState({
          isLoading: false,
        });
      }
    }

    if (hasReturnUrlValuesInQuery(router.query)) {
      if (router.query.redirect_status !== 'succeeded') {
        cancelPaymentOnDrupal(router.query.order_id, router.query.payment_id);
        dispatch(
          donationFormSubmitError({
            errorMessage:
              "Sorry, we couldn't complete your donation. Please review your details and try again or select an alternative payment method.",
            subline: unexpectedErrorMessage(),
          }),
        );
      } else {
        const intentId =
          router.query.type === 'single' ? router.query.payment_intent : router.query.setup_intent;
        await this.handleSuccessReturn(router.query.order_id, router.query.payment_id, intentId);
      }
      this.setState({
        isLoading: false,
      });
    }
  }

  componentWillUnmount() {
    const { dispatch = () => {} } = this.props;
    // For security reasons reset donation form storage immediately after
    // the form is closed.
    dispatch(donationFormReset());
  }

  async handleSuccessReturn(orderID, paymentId, intentId) {
    const {
      router,
      appealUrl,
      showFundraisingCosts = false,
      extraFeatures = [],
      appealId,
      title,
    } = this.props;

    try {
      // This function always return success.
      const completeOrderResponse = await completeOrder(orderID, paymentId);
      const suppressPurchaseEvent = completeOrderResponse?.captureStatusCode === 409;
      const navigationType =
        typeof performance === 'undefined'
          ? null
          : (performance.getEntriesByType?.('navigation')?.[0]?.type ?? null);
      const suppressPurchaseEventByNavigation =
        navigationType === 'reload' || navigationType === 'back_forward';

      // We are passing all necessary parameters in return_url, so that we
      // can complete order quicker, without additional request to the backend.
      const isGiftAid = (router.query.giftaid && router.query.giftaid === 'true') || false;
      // The code below is mostly copy of DonationFormPage.componentDidUpdate()
      // but with multiple adjustments. In theory, some parts can be encapsulated.
      if (DEFAULT_COUNTRY_CODE === 'GB' && !isGiftAid) {
        // For UK region we want to show giftaid form on Thank you page.
        // Also, we want to have expiration time for this form, so
        // we need to know a time when the order was created.
        const currentTime = +new Date();
        window.sessionStorage.setItem(`cw_order_${orderID}`, `${currentTime}:false`);
      }

      // Clear the donation attempt URL so the ReturnToDonationPopup is not displayed.
      window.sessionStorage.removeItem('donationFormAttempt');

      // Redirect user to thank you page as soon as possible.
      const donationType = router.query.type === 'monthly' ? RG : SD;
      const thankYouPage = buildStepPath(appealUrl, 'thank-you', {
        orderID,
        type: donationType,
      });
      const route = {
        pathname: router.pathname,
        query: Object.assign(router.query, { orderID }),
      };

      // Now we can relax and handle GTM Stuff.
      // Making a secure request to the backend to obtain order details.
      const responseMetadata = await fetch(`/commerce/order/${orderID}/metadata`, {
        query: { payment_id: paymentId, intent_id: intentId },
      });

      if (responseMetadata.body?.data) {
        const formValues = responseMetadata.body.data;

        const formContactPreferences = formValues.contact_preferences;
        const viewedPreferences = getPreferencesForView(
          formValues.is_short_form,
          formValues.is_short_form_with_optin,
        );
        // Contact preferences from backend don't take into account default
        // preference value, and it leads to wrong results in normalisePreferencesValues().
        let alignedContactPreferences = [];
        viewedPreferences.forEach(({ value, valueBackend }) => {
          if (
            (formContactPreferences.includes(value) && valueBackend !== 'denied') ||
            (formContactPreferences.includes(value) &&
              DEFAULT_COUNTRY_CODE === 'GB' &&
              value === 'post' &&
              valueBackend === 'denied')
          ) {
            alignedContactPreferences.push(value);
          }
        });
        const contactPreferences = normalisePreferencesValues(
          alignedContactPreferences,
          viewedPreferences,
        );

        const amount = formValues.fundraisingCostsApplied
          ? formValues.totalAmount
          : formValues.amount[formValues.donation_type];

        // Store donation details to display them on the 'Thank you' page.
        // Few body blocks use the data.
        sessionStorageSetItem(
          'donationDetails',
          JSON.stringify({
            name: formValues.first_name,
            orderId: orderID,
            totalAmount: amount,
          }),
        );

        router
          .push(route, thankYouPage, { shallow: true })
          // Shallow routing doesn't redirect to the top of the page.
          // Consider moving this code to onRouteChangeComplete event handler.
          .then(() => window.scrollTo(0, 0));

        if (!suppressPurchaseEvent && !suppressPurchaseEventByNavigation) {
          // Send purchase event to GTM.
          const transactionData = {
            id: orderID,
            revenue: amount,
          };

          const isFundraisingCostsAsked =
            showFundraisingCosts && isFundraisingCostsVisible(formValues);

          const showCompanyDonation = extraFeatures.includes('company_donation');
          const isCompanyDonation =
            formValues.company_donation && formValues.company_donation[0] === 'company_donation';

          const products = [
            {
              name: title,
              id: appealId,
              price: amount,
              category:
                formValues.type === 'recurring_donation' ? 'Monthly donation' : 'Single donation',
              variant: formValues.type,
              quantity: 1,
              contact_preferences: contactPreferences,
              source_code: formValues.source_code || '',
              fundraising_costs_applied: isFundraisingCostsAsked
                ? formValues.fundraisingCostsApplied
                : 'not_asked',
              fundraising_costs_amount: formValues.fundraisingCostsAmount,
              fundraising_costs_formula: getFundraisingCostsExplanation(
                isFundraisingCostsAsked ? formValues.fundraisingCostsFormula : 'not_asked',
              ),
              fundraising_costs_element_type: isFundraisingCostsAsked ? 'dropdown' : 'not_asked',
              company_donation: showCompanyDonation ? isCompanyDonation : 'not_asked',
              gift_aid: formValues.giftAid,
              gift_aid_amount: getGiftAidAmount(amount, formValues.giftAid),
            },
          ];
          gtmPushPurchase(transactionData, products);
        }
      } else {
        router
          .push(route, thankYouPage, { shallow: true })
          // Shallow routing doesn't redirect to the top of the page.
          // Consider moving this code to onRouteChangeComplete event handler.
          .then(() => window.scrollTo(0, 0));
      }
    } catch (error) {
      rollbar.error(`Error during completing order with id ${orderID}.`, { error });
    }
  }

  render() {
    const {
      appealId,
      appealUrl,
      minAmount,
      defaultDonationType = SD,
      defaultAmount = 0,
      sourceCodes,
      sourceCodesRG = [],
      supportsSingle,
      supportsRecurring,
      dispatch = () => {},
      formStateValues = {},
      enabledSteps,
      isLocked = false,
      router,
      showRevolutStripe = false,
      extraFeatures = [],
      stepsData,
      emailOptinDescription,
      stripeButtonAvailability,
      impactsSingleDonation = [],
      impactsMonthlyDonation = [],
      donationFormTitle = '',
      isRecaptchaEnabled,
      showFundraisingCosts = false,
      orderID = null,
      nonce,
    } = this.props;

    const { email, name, isLoading } = this.state;

    if (isLoading) {
      return (
        <Popup isVisible className="loading-screen" onPopupClose={() => {}}>
          <LoadingText text="Please wait" />
        </Popup>
      );
    }

    // Initial form values.
    const initialValues = {};
    // Put appeal ID as a hidden form value.
    // Later it will be used to send donation form to the backend in Saga.
    // See ./store/sagas/donationForm.js
    initialValues.appealId = appealId;
    // Appeal URL is needed to generate "return_url" for payments with redirect.
    initialValues.appealUrl = appealUrl;

    // Prepare initial donation type value.
    // In case of multi-type appeals use single donation by default.
    initialValues.type = supportsSingle ? SD : RG;
    // Default donation type can be overwritten with a prop.
    if (defaultDonationType === RG && supportsRecurring) {
      initialValues.type = defaultDonationType;
    }

    // Current donation type.
    const donationType = formStateValues.type ? formStateValues.type : initialValues.type;

    let formSourceCodes = sourceCodes;
    if (donationType === RG && sourceCodesRG.length) {
      formSourceCodes = sourceCodesRG;
    }

    // Set default Contact Preferences step values. May be changed if shortform & opt-in are active.
    let preferencesText = stepsData.preferences.text;

    let isShortform = false;
    let isShortformWithOptin = false;

    if (name && email) {
      initialValues.dsn = router.query.dsn;
      initialValues.email = email;
      initialValues.first_name = name;
      // Source code will be applied several lines below.
      isShortform = true;

      const { optin } = router.query;
      initialValues.optin = optin ? parseInt(optin, 10) : optin;

      // If this is a shortform and an optin has been activated, tailor the preferences step.
      if (initialValues.optin === 1) {
        preferencesText = emailOptinDescription;
        isShortformWithOptin = true;
      }
    }
    // Add viewed preferences options to form values to be sure
    // witch preferences were displayed to a user.
    initialValues.viewedPreferencesOptions = getPreferencesForView(
      isShortform,
      isShortformWithOptin,
    );

    // Pre-populate amount field with the default value if exists.
    if (defaultAmount > 0) {
      initialValues.amount = defaultAmount;
    }

    // Pre-populate amount field with the value from url.
    if (router.query.amount && !isNaN(router.query.amount) && router.query.amount > 0) {
      initialValues.amount = router.query.amount;
    }

    // Get default country from the environment.
    initialValues.address = { country: getDefaultCountry() };
    const currentCountry = formStateValues.address
      ? formStateValues.address.country.value
      : initialValues.address.country.value;

    const attribution = getAttribution();
    // TODO: Revise and remove first condition if needed,
    //  as part of https://www.pivotaltracker.com/story/show/188587772.
    // Validation for source parameter in shorform URL is disabled temporarily,
    // see cnet/utils/shortform.js, applying it separately.
    if (isShortform) {
      initialValues.source_code = router.query.source;
    }
    // Pre-populate the source code field if the valid value is provided in URL.
    else if (router.query.source && isValidGUID(router.query.source)) {
      initialValues.source_code = router.query.source;
    }
    // Pre-populate the source code field if the source code for this appeal
    // is available in session data.
    else if (attribution && attribution.appeal_id && attribution.appeal_id === appealId) {
      initialValues.source_code = attribution.source_code;
    }
    // Pre-populate the source code field for general donation
    // if any source code is available in session data.
    else if (attribution && isGeneralDonation(appealId)) {
      initialValues.source_code = attribution.source_code;
    }
    // Pre-populate the source code field if there's only 1 source code
    // in the appeal config.
    else if (formSourceCodes.length === 1) {
      initialValues.source_code = formSourceCodes[0].value;
    }

    // Mirrors the visibility condition used in AddressStep: the field is
    // only shown to the donor when it isn't already pre-populated above.
    const wasSourceCodeFieldShown = Boolean(formSourceCodes) && !initialValues.source_code;
    registerSourceCodeField({
      sourceCodes: formSourceCodes,
      wasFieldShown: wasSourceCodeFieldShown,
    });

    const paymentMethods = filterActiveMethods({
      mode: PAYMENT_MODE,
      donationType,
      region: DEFAULT_COUNTRY_CODE,
      stripeButtonAvailability,
      showRevolutStripe,
    }).map((method) => ({
      value: method.id,
      label: method.label,
      icon: method.icon,
    }));

    // Set first payment method as default value.
    if (paymentMethods.length > 0) {
      const [paymentMethod] = paymentMethods;
      initialValues.payment_method = paymentMethod.value;
    }

    // Apply some defaults (currently limited to initial methods only).
    const initialMethod = methods.find((el) => el.id === initialValues.payment_method);
    if (initialMethod && initialMethod.defaults) {
      initialValues.payment_options = initialMethod.defaults;
    }

    // Initialize form with default values for the contact preferences.
    initialValues.contact_preferences = [];

    // Passing fundraising cost element display state for using in GTM event.
    initialValues.show_fundraising_costs = showFundraisingCosts;

    const showCompanyDonation = extraFeatures.includes('company_donation');
    initialValues.show_company_donation = showCompanyDonation;

    if (
      showFundraisingCosts &&
      initialValues.type === 'single_donation' &&
      initialValues.payment_method
    ) {
      // We need to know amount to be able to re-calculate transaction fee
      // that we display as part of the label in the Select field.
      // The null value will be automatically updated
      // in the FundraisingCostsVariantB component.
      initialValues.fundraising_costs = { value: null, label: '' };
    }

    // By default, we always show donation form submit button at the end of
    // multistep journey. However, in certain cases (for example, if it's
    // PayPal payment), the button should be hidden and replaced by other
    // custom CTA to submit the form.
    let hideSubmitButton = false;
    if (
      formStateValues.payment_method &&
      /^(paypal|decoupled_stripe_button)_/.test(formStateValues.payment_method)
    ) {
      hideSubmitButton = true;
    }

    // Define steps using step machine names from DONATION_FORM_STEPS constant.
    const stepsDefinition = {
      amount: (
        <AmountPersonalInfoStep
          heading={stepsData.amount.heading}
          text={stepsData.amount.text}
          minAmount={minAmount > 1 ? minAmount : 1}
          donationType={donationType}
          name={initialValues.first_name}
          email={initialValues.email}
          extraFeatures={extraFeatures}
          nextButtonLabel={isShortform ? 'Proceed to payment' : 'Proceed to address'}
          showCompanyDonation={showCompanyDonation}
        />
      ),
      address: (
        <AddressStep
          heading={stepsData.address.heading}
          text={stepsData.address.text}
          country={currentCountry}
          sourceCode={initialValues.source_code ?? ''}
          sourceCodes={formSourceCodes}
          nextButtonLabel="Proceed to payment"
        />
      ),
      preferences: (
        <ContactPreferencesStep
          heading={stepsData.preferences.heading}
          text={preferencesText}
          options={initialValues.viewedPreferencesOptions}
          isShortformWithOptin={initialValues.optin === 1}
          nextButtonLabel="Proceed to payment"
        />
      ),
      giftaid: (
        <GiftAidStep
          heading={stepsData.giftaid.heading}
          text={stepsData.giftaid.text}
          // onAccept and onCancel functions will be added by
          // FormWizard component.
          onCancel={() => null}
          onAccept={() => null}
        />
      ),
      payment: (
        <PaymentsStep
          heading={stepsData.payment.heading}
          text={stepsData.payment.text}
          paymentMethods={paymentMethods}
        />
      ),
    };

    // Store step metadata in form itself.
    initialValues.step = { name: 'amount', index: 0 };

    // Keep all form steps here. It needs for dynamic forms steps calculation.
    const steps = [];

    // Filter out inactive steps.
    let filteredEnabledSteps = Object.keys(stepsDefinition).filter((stepName) =>
      enabledSteps.includes(stepName),
    );

    if (
      formStateValues.company_donation?.includes('company_donation') &&
      formStateValues.company_name
    ) {
      // Filter out GiftAid step for organisations.
      filteredEnabledSteps = Object.keys(stepsDefinition).filter(
        (stepName) => stepName !== 'giftaid',
      );
    }

    // Give each step name and key.
    filteredEnabledSteps.forEach((stepName) => {
      steps.push(
        <FormWizard.Step key={stepName} name={stepName}>
          {stepsDefinition[stepName]}
        </FormWizard.Step>,
      );
    });

    const initialStepsForStepIndicator = [];
    filteredEnabledSteps.forEach((stepName, index) => {
      // Render step indicator with steps that have stepIndicatorName.
      // Also we are using index of step in FormWizard to know current step for step indicator.
      if (DONATION_FORM_STEPS[stepName] && DONATION_FORM_STEPS[stepName].stepIndicatorName) {
        initialStepsForStepIndicator.push({
          name:
            isShortform && stepName === 'amount'
              ? 'DONATION AMOUNT'
              : DONATION_FORM_STEPS[stepName].stepIndicatorName,
          index,
          clickable: false,
          completed: false,
        });
      }
    });

    // Save a session value that stores the URL to return to the donation.
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(router.asPath, window.location.origin);
      if (formStateValues.amount) {
        currentUrl.searchParams.set('amount', formStateValues.amount);
      }

      // Make sure "Thank You" page does not count as donation form attempt.
      if (!currentUrl.toString().includes('/thank-you')) {
        window.sessionStorage.setItem('donationFormAttempt', currentUrl.toString());
      }
    }

    return (
      <MakeAsyncFunction
        // TODO: Replace MakeAsyncFunction with createAsyncThunk
        // from redux-toolkit.
        listener={promiseListener}
        start="donationForm/donationFormSubmit"
        resolve="donationForm/donationFormSubmitSuccess"
        reject="donationForm/donationFormSubmitError"
      >
        {(onSubmit) => {
          return (
            <>
              <Beforeunload
                onBeforeunload={(event) => {
                  // Do not show alert when redirecting to Revolut payment page.
                  if (
                    formStateValues.payment_method.startsWith('decoupled_stripe_revolut') &&
                    orderID
                  ) {
                    return;
                  }
                  event.preventDefault();
                  // The 1ms delay is to add the code to the UI queue.
                  // The dialog will stop the execution and only continue with
                  // the nested setTimeout if the user cancels the dialog.
                  setTimeout(function () {
                    setTimeout(function () {
                      gtmPushReturnToDonationPrompt();
                    }, 1000);
                  }, 1);
                }}
              />
              <FormWizard
                onSubmit={onSubmit}
                onChange={(state) => dispatch(donationFormStateUpdate(state))}
                onButtonClick={(errors) => dispatch(donationFormStepValidationErrors(errors))}
                initialValues={initialValues}
                hideSubmitButton={hideSubmitButton}
                submitButtonLabel="Complete donation"
                queryParams={router.query}
                initialStepsForStepIndicator={initialStepsForStepIndicator}
                isRecaptchaEnabled={isRecaptchaEnabled}
                impactsSingleDonation={impactsSingleDonation}
                impactsMonthlyDonation={impactsMonthlyDonation}
                formTitle={donationFormTitle}
                isLocked={isLocked}
                extraFeatures={extraFeatures}
                showFundraisingCosts={showFundraisingCosts}
                nonce={nonce}
              >
                {steps}
              </FormWizard>
              {isLocked && (
                <Popup
                  isVisible
                  className="loading-screen submitting-donation"
                  onPopupClose={() => {}}
                >
                  <LoadingText text="Please wait" />
                </Popup>
              )}
              <PaymentModeWarning />
            </>
          );
        }}
      </MakeAsyncFunction>
    );
  }
}

DonationForm.propTypes = {
  appealId: PropTypes.number.isRequired,
  appealUrl: PropTypes.string.isRequired,
  // Appeal title.
  title: PropTypes.string.isRequired,
  donationFormTitle: PropTypes.string,
  minAmount: PropTypes.number.isRequired,
  sourceCodes: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  sourceCodesRG: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  showRevolutStripe: PropTypes.bool,
  supportsSingle: PropTypes.bool.isRequired,
  supportsRecurring: PropTypes.bool.isRequired,
  defaultDonationType: PropTypes.oneOf([RG, SD]),
  defaultAmount: PropTypes.number,
  dispatch: PropTypes.func,
  formStateValues: PropTypes.shape(),
  isLocked: PropTypes.bool,
  enabledSteps: PropTypes.arrayOf(PropTypes.string).isRequired,
  router: PropTypes.shape().isRequired,
  extraFeatures: PropTypes.arrayOf(PropTypes.string),
  stepsData: PropTypes.shape({
    amount: PropTypes.shape({
      heading: PropTypes.string,
      text: PropTypes.string,
    }).isRequired,
    address: PropTypes.shape({
      heading: PropTypes.string,
      text: PropTypes.string,
    }).isRequired,
    preferences: PropTypes.shape({
      heading: PropTypes.string,
      text: PropTypes.string,
    }).isRequired,
    source: PropTypes.shape({
      heading: PropTypes.string,
      text: PropTypes.string,
    }).isRequired,
    giftaid: PropTypes.shape({
      heading: PropTypes.string,
      text: PropTypes.string,
    }).isRequired,
    payment: PropTypes.shape({
      heading: PropTypes.string,
      text: PropTypes.string,
    }).isRequired,
  }).isRequired,
  emailOptinDescription: PropTypes.string.isRequired,
  stripeButtonAvailability: PropTypes.shape({ applePay: PropTypes.bool }),
  impactsSingleDonation: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      text: PropTypes.string,
    }),
  ),
  impactsMonthlyDonation: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      text: PropTypes.string,
    }),
  ),
  showFundraisingCosts: PropTypes.bool,
  isRecaptchaEnabled: PropTypes.bool.isRequired,
  orderID: PropTypes.number,
  nonce: PropTypes.string,
};

const mapStateToProps = ({ donationForm }) => ({
  formStateValues: donationForm.form.values,
  isLocked: donationForm.isLocked,
  stripeButtonAvailability: donationForm.stripeButtonAvailability,
  orderID: donationForm.orderID,
});

export default connect(mapStateToProps)(withRouter(DonationForm));
